import { Alert, Modal, Typography } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import type { SecretGrant } from '@/api/subscriptions';
import { Term } from '@/help/Term';

const { Paragraph, Text } = Typography;

/**
 * The one-time secret display (create / rotate). The raw `whsec_` value exists ONLY in this
 * response — the server stores a fingerprint and can never re-show it (the contract: derived,
 * no secret at rest). So this modal is deliberately hard to dismiss by accident (no mask close)
 * and says exactly what losing the value means. The UI never invents any other secret display.
 */
export function SecretGrantModal({ grant, onClose }: { grant: SecretGrant | null; onClose: () => void }) {
  return (
    <Modal
      open={grant !== null}
      onCancel={onClose}
      onOk={onClose}
      okText="I stored it"
      cancelButtonProps={{ style: { display: 'none' } }}
      maskClosable={false}
      title={
        <span>
          <KeyOutlined style={{ marginRight: 8 }} />
          Signing secret for{' '}
          <Text className="ol-mono">{grant?.subscription.name}</Text>
        </span>
      }
    >
      {grant && (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Shown once — store it now"
            description={
              <span>
                This <Term id="webhook-secret">webhook secret</Term> is derived, never stored, and
                cannot be re-displayed. If it is lost, rotate the secret to mint a new value.
              </span>
            }
          />
          <Paragraph
            copyable
            code
            style={{ fontSize: 12.5, wordBreak: 'break-all', marginBottom: 8 }}
            aria-label="One-time signing secret"
          >
            {grant.secret}
          </Paragraph>
          {grant.secretNote && (
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
              Server note: {grant.secretNote}
            </Paragraph>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Secret version {grant.subscription.secretVersion} · fingerprint{' '}
            <span className="ol-mono">{grant.subscription.secretFingerprint}</span> (what support
            sees — never the secret). Deliveries are signed HMAC-SHA256 over{' '}
            <span className="ol-mono">timestamp + &quot;.&quot; + body</span>{' '}
            (<span className="ol-mono">X-OL-Signature</span>).
          </Text>
        </>
      )}
    </Modal>
  );
}
